import { TraceBehavior } from "./tracebehavior";
import { BoxColliderData } from "../scenedata/behaviors/boxcolliderdata";
import { Vec3 } from "../scenedata/common";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Matrix } from "@babylonjs/core/Maths/math.vector";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";

import { Logger } from "../utils/Logger";

/** Suffix used to identify collider picker meshes */
export const COLLIDER_MESH_SUFFIX = "__collider";

/** Suffix used to identify edit-mode selection meshes (for meshless behaviors like Text, ParticleSystem) */
export const SELECTION_MESH_SUFFIX = "__selection";

/**
 * Least depth, in metres, of a collider auto-sized from a selection proxy.
 * A glyph plane is a centimetre or two deep, which a tap ray at a slight angle
 * can miss; the same floor Studio's fitCollider used.
 */
const MIN_PROXY_COLLIDER_DEPTH = 0.05;

/**
 * Box collider behavior for physics and interaction detection.
 * Defines a box-shaped collision volume.
 *
 * Creates an invisible picker mesh for selection in play mode.
 * The picker mesh matches the collider's size and center offset.
 *
 * ## Usage
 *
 * ```typescript
 * const collider = sceneObject.getBehavior(BoxCollider);
 * const data = collider.getData();
 * data.size = { x: 2, y: 1, z: 1 };
 * data.center = { x: 0, y: 0.5, z: 0 };
 * await collider.commit(data);
 * ```
 */
export class BoxCollider extends TraceBehavior<BoxColliderData> {
    static readonly behaviorType = "BoxCollider" as const;

    /** Invisible mesh used for picking in play mode */
    private _pickerMesh: Mesh | null = null;

    /**
     * Get the picker mesh (for external access, e.g., mode switching).
     */
    public getPickerMesh(): Mesh | null {
        return this._pickerMesh;
    }

    protected onAttach(target: TransformNode): void {
        // If size is unspecified, derive from model bounds or default to unit size
        if (!this._resolved.size) {
            const bounds = this._getModelBounds(target);
            const size = bounds?.size ?? [1, 1, 1] as Vec3;
            const center = this._resolved.center ?? bounds?.center ?? [0, 0, 0] as Vec3;
            void this.commit({ size, center });
            return; // commit triggers applyCommit → _createPickerMesh
        }
        this._createPickerMesh(target);
    }

    protected onDetach(): void {
        this._disposePickerMesh();
    }

    protected async applyCommit(_patch: Partial<BoxColliderData>): Promise<void> {
        if (this.attachedNode) {
            // Recompute native policy for the rebuilt mesh, including hover
            // and Renderer.pickThrough, without a play-mode toggle.
            this._disposePickerMesh();
            this._createPickerMesh(this.attachedNode);
            this.sceneObject.traceScene.refreshMeshPickability?.(this.sceneObject);
        }
    }

    /**
     * Toggle debug visualization of the collider mesh.
     * When visible, shows a semi-transparent green wireframe box.
     */
    public setDebugVisible(visible: boolean): void {
        if (!this._pickerMesh) return;

        // Use visibility (0-1) instead of isVisible to keep picking working
        this._pickerMesh.visibility = visible ? 0.3 : 0;

        if (visible && !this._pickerMesh.material) {
            const mat = new StandardMaterial(
                `${this.sceneObject.name}_collider_debug_mat`,
                this.sceneObject.babylonScene
            );
            mat.diffuseColor = new Color3(0, 1, 0); // Green
            mat.wireframe = true;
            this._pickerMesh.material = mat;
        }
    }

    private _createPickerMesh(parent: TransformNode): void {
        const data = this._resolved;
        const size = data.size ?? [1, 1, 1];
        const center = data.center ?? [0, 0, 0];

        Logger.debug(`[BoxCollider] Creating picker mesh for "${this.sceneObject.name}": size=(${size[0]}, ${size[1]}, ${size[2]}), center=(${center[0]}, ${center[1]}, ${center[2]})`);

        // Create invisible box mesh for picking
        this._pickerMesh = MeshBuilder.CreateBox(
            `${this.sceneObject.name}${COLLIDER_MESH_SUFFIX}`,
            { width: size[0], height: size[1], depth: size[2] },
            this.sceneObject.babylonScene
        );

        // Position at center offset
        this._pickerMesh.position.set(center[0], center[1], center[2]);

        // Parent to the transform node
        this._pickerMesh.parent = parent;

        // Make transparent but pickable
        // Note: isVisible=false causes Babylon to skip picking, so we use visibility=0 instead
        this._pickerMesh.visibility = 0;
        this._pickerMesh.isPickable = true;

        Logger.debug(`[BoxCollider] Picker mesh created: "${this._pickerMesh.name}", isPickable=${this._pickerMesh.isPickable}`);
    }

    /**
     * Compute bounding box from child meshes (e.g. a loaded Model).
     * Each mesh's local BB corners are transformed into the parent's local
     * space via meshWorld * invParent, which correctly accounts for any
     * intermediate transforms (armatures, bone hierarchies, pivot nodes).
     * An object with no real mesh (Text, ParticleSystem) is measured by its
     * `__selection` proxy instead, with a minimum depth. Returns null if
     * neither has geometry.
     */
    private _getModelBounds(parent: TransformNode): { size: Vec3; center: Vec3 } | null {
        const meshes = parent.getChildMeshes(false);
        const real = BoxCollider._boundsOf(parent, meshes.filter(
            m => !m.name.endsWith(COLLIDER_MESH_SUFFIX) && !m.name.endsWith(SELECTION_MESH_SUFFIX),
        ));
        if (real) return real;

        const proxy = BoxCollider._boundsOf(parent, meshes.filter(m => m.name.endsWith(SELECTION_MESH_SUFFIX) && m.isEnabled(false)));
        if (!proxy) return null;
        return { size: [proxy.size[0], proxy.size[1], Math.max(proxy.size[2], MIN_PROXY_COLLIDER_DEPTH)], center: proxy.center };
    }

    /** Bounds of `meshes` in `parent`'s local space; null when none has geometry. */
    private static _boundsOf(parent: TransformNode, meshes: AbstractMesh[]): { size: Vec3; center: Vec3 } | null {
        if (meshes.length === 0) return null;

        parent.computeWorldMatrix(true);
        const worldMatrix = parent.getWorldMatrix();
        const invertible = worldMatrix.determinant() !== 0;
        const invParent = invertible ? Matrix.Invert(worldMatrix) : null;

        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        const tmp = new Vector3();

        for (const mesh of meshes) {
            mesh.computeWorldMatrix(true);
            const bb = mesh.getBoundingInfo().boundingBox;
            const localMin = bb.minimum;
            const localMax = bb.maximum;
            // Skip meshes with degenerate bounds
            if (localMin.equalsToFloats(0, 0, 0) && localMax.equalsToFloats(0, 0, 0)) {
                continue;
            }

            if (invParent) {
                // mesh-local → parent-local: mesh.world * invParent
                const meshToParent = mesh.getWorldMatrix().multiply(invParent);

                // Transform all 8 BB corners into parent-local space
                for (let cx = 0; cx <= 1; cx++) {
                    for (let cy = 0; cy <= 1; cy++) {
                        for (let cz = 0; cz <= 1; cz++) {
                            tmp.set(
                                cx === 0 ? localMin.x : localMax.x,
                                cy === 0 ? localMin.y : localMax.y,
                                cz === 0 ? localMin.z : localMax.z,
                            );
                            Vector3.TransformCoordinatesToRef(tmp, meshToParent, tmp);
                            minX = Math.min(minX, tmp.x);
                            minY = Math.min(minY, tmp.y);
                            minZ = Math.min(minZ, tmp.z);
                            maxX = Math.max(maxX, tmp.x);
                            maxY = Math.max(maxY, tmp.y);
                            maxZ = Math.max(maxZ, tmp.z);
                        }
                    }
                }
            } else {
                // Non-invertible parent (e.g. zero scale) — fall back to raw mesh-local bounds
                minX = Math.min(minX, localMin.x);
                minY = Math.min(minY, localMin.y);
                minZ = Math.min(minZ, localMin.z);
                maxX = Math.max(maxX, localMax.x);
                maxY = Math.max(maxY, localMax.y);
                maxZ = Math.max(maxZ, localMax.z);
            }
        }

        if (!isFinite(minX)) return null;

        return {
            size: [maxX - minX, maxY - minY, maxZ - minZ],
            center: [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2],
        };
    }

    private _disposePickerMesh(): void {
        if (this._pickerMesh) {
            this._pickerMesh.dispose();
            this._pickerMesh = null;
        }
    }
}
